import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, Card, Alert } from 'react-bootstrap';
import FroggySpinner from '../components/common/FroggySpinner';
import { getPromoBySlug } from '../services/api';
import { createProductPath } from '../utils/slugUtils';
import ReactMarkdown from 'react-markdown';
import SEO from '../components/SEO';
import VariationCard from '../components/Products/VariationCard';
import './PromoPage.css';
import { formatProductName } from '../utils/productName';

const PromoPage = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [promoData, setPromoData] = useState(null);

  useEffect(() => {
    const fetchPromoData = async () => {
      try {
        setLoading(true);
        const data = await getPromoBySlug(slug);
        setPromoData(data);
        setError(null);
      } catch (err) {
        console.error('[PromoPage] Error fetching promo:', err);
        setError(err.response?.data?.error || 'Failed to load promotion');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPromoData();
    }
  }, [slug]);

  if (loading) {
    return (
      <Container className="text-center py-5">
        <FroggySpinner size="lg" />
        <p className="mt-3">Loading promotion...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Promotion Not Found</Alert.Heading>
          <p>{error}</p>
          <Link to="/" className="ff-btn ff-btn--primary">
            Return to Homepage
          </Link>
        </Alert>
      </Container>
    );
  }

  const {
    name,
    customer_facing_title,
    subtitle,
    code,
    description,
    long_body,
    hero_image_url,
    featured_items,
    care_guide,
    related_promotions
  } = promoData || {};

  // Use customer_facing_title if available, fall back to name
  const displayTitle = customer_facing_title || name;

  // #101a — sibling disambiguation for featured cards (same rule as the
  // PDP grid): these cards render with showMorphNames off, so two
  // different-morph variations sharing a variation-type heading ("Unsexed
  // Adult" twice) read as duplicate listings. When that collision exists,
  // pass a morph-qualified one-of-a-kind badge label ("Burgundy — one of
  // a kind"); VariationCard decides WHETHER to badge (individual-mode,
  // exactly one animal).
  const productFeaturedItems = (featured_items || []).filter(
    (item) => item.type === 'product' && item.variation && item.product
  );

  // #101b — the "View all <species>" cross-link is SECTION-level now:
  // rendered once per distinct featured product, right-aligned under the
  // featured-items Row. Rendering it inside the first card's grid column
  // made that column ~32px taller and the link looked orphaned.
  const viewAllTargets = [];
  productFeaturedItems.forEach((item) => {
    if (!viewAllTargets.some((t) => t.id === item.product.id)) {
      viewAllTargets.push({
        id: item.product.id,
        label: item.product.species?.common_names?.[0] || formatProductName(item.product),
      });
    }
  });

  // Determine "How this deal works" text
  const hasDescription = description && description.trim().length > 0;
  let howToText;
  if (hasDescription) {
    howToText = description.trim();
  } else if (code) {
    howToText = `Use coupon code ${code} at checkout. Add any eligible item below to your cart and enter the code on the checkout page to apply your discount.`;
  } else {
    howToText = 'Discount is applied automatically when you add any eligible items below to your cart—no coupon code needed.';
  }

  return (
    <>
      <SEO
        title={displayTitle || 'Promotion'}
        description={
          subtitle && code
            ? `${subtitle} - Use code ${code} at checkout. ${description || ''}`
            : subtitle || description || 'Special promotion on quality amphibians and vivarium supplies'
        }
        canonical={`https://feelingfroggy.llc/promo/${slug}`}
        keywords={`${displayTitle}, promo, discount, ${code ? 'coupon code ' + code : 'sale'}, dart frogs, amphibians`}
        ogImage={hero_image_url || 'https://feelingfroggy.llc/logos/ff_original_96px.png'}
        ogType="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SpecialAnnouncement",
          "name": displayTitle,
          "description": subtitle || description,
          "category": "https://schema.org/SaleEvent",
          "url": `https://feelingfroggy.llc/promo/${slug}`,
          ...(code && {
            "text": `Use coupon code: ${code}`
          })
        }}
      />

      {/* Hero Header - Above Image */}
      <div className="promo-hero-header">
        <Container>
          <h1 className="promo-hero-title">{displayTitle}</h1>
          {subtitle && (
            <p className="promo-hero-subtitle">{subtitle}</p>
          )}
          {code && (
            <div className="promo-code-pill-hero">
              CODE: {code}
            </div>
          )}
        </Container>
      </div>

      {/* Hero Image - Below Header */}
      <div
        className="promo-hero-image"
        style={{
          backgroundImage: hero_image_url ? `url(${hero_image_url})` : 'none',
          backgroundColor: hero_image_url ? 'transparent' : 'var(--ff-brandBlue)'
        }}
      />

      <Container className="py-5">
        <Row>
          {/* Main Content */}
          <Col lg={care_guide ? 8 : 12}>
            {/* Long Body Content */}
            {long_body && (
              <div className="promo-content mb-5">
                <ReactMarkdown>{long_body}</ReactMarkdown>
              </div>
            )}

            {/* How This Deal Works - Above Featured Items */}
            {howToText && (
              <div className="promo-how-to-box" aria-label="How this deal works">
                <h2 className="promo-how-to-title">How this deal works</h2>
                <p className="promo-how-to-text">{howToText}</p>
              </div>
            )}

            {/* Featured Items Grid - Using VariationCard */}
            {featured_items && featured_items.length > 0 && (
              <div className="promo-featured-items mb-5">
                <h2 className="mb-4">Featured Items</h2>
                <Row>
                  {featured_items.map((item) => {
                    // Only render product items (variations) - skip species items for now
                    if (item.type === 'product' && item.variation && item.product) {
                      return (
                        <Col key={item.id} xs={12} sm={6} md={4} className="mb-4">
                          <VariationCard
                            variation={item.variation}
                            product={item.product}
                            showMorphNames={false}
                          />
                        </Col>
                      );
                    }

                    // Fallback for species items (keep old card style)
                    if (item.type === 'species') {
                      return (
                        <Col key={item.id} xs={12} sm={6} md={4} className="mb-4">
                          <Card className="promo-item-card h-100">
                            {item.image_url && (
                              <Card.Img
                                variant="top"
                                src={item.image_url}
                                alt={item.name}
                                className="promo-item-card-img"
                              />
                            )}
                            <Card.Body className="d-flex flex-column">
                              <span className="ff-badge ff-badge--success mb-2 align-self-start">
                                Species
                              </span>
                              <Card.Title className="h6">{item.name}</Card.Title>
                              {/* a link styled as the family secondary — never
                                  a <button> nested inside an <a> */}
                              <Link
                                to={item.link}
                                className="ff-btn ff-btn--secondary ff-btn--sm ff-btn--block mt-auto"
                              >
                                View Care Guide
                              </Link>
                            </Card.Body>
                          </Card>
                        </Col>
                      );
                    }

                    return null;
                  })}
                </Row>
                {/* #101b — section-level cross-link (operator-found gap:
                    the cards only add to cart, they never linked
                    anywhere). One link per distinct featured product,
                    right-aligned directly under the grid. */}
                {viewAllTargets.length > 0 && (
                  <div className="promo-view-all">
                    {viewAllTargets.map((target) => (
                      <Link
                        key={target.id}
                        to={createProductPath(target.id)}
                        className="promo-view-all-link"
                      >
                        View all {target.label} →
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Related Promotions Section */}
            {related_promotions && related_promotions.length > 0 && (
              <section className="promo-other-deals promo-related-deals mb-5">
                <h2 className="mb-4">Other Active Deals in This Sale</h2>
                <Row>
                  {related_promotions.map((relatedPromo) => (
                    <Col key={relatedPromo.id} xs={12} md={6} className="mb-3">
                      <Card className="promo-related-card">
                        <Card.Body>
                          <Card.Title className="h5">{relatedPromo.name}</Card.Title>
                          {relatedPromo.short_description && (
                            <Card.Text className="text-muted small">
                              {relatedPromo.short_description}
                            </Card.Text>
                          )}
                          <div className="d-flex align-items-center justify-content-between mt-3">
                            <span className="price-badge-sale fs-6">
                              {relatedPromo.discount_summary}
                            </span>
                            {relatedPromo.has_code && relatedPromo.code && (
                              <span className="ff-badge ff-badge--secondary fs-6">
                                Code: {relatedPromo.code}
                              </span>
                            )}
                          </div>
                          {relatedPromo.slug && (
                            <Link
                              to={`/promo/${relatedPromo.slug}`}
                              className="ff-btn ff-btn--secondary ff-btn--sm ff-btn--block mt-3"
                            >
                              View Promo Page
                            </Link>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </section>
            )}
          </Col>

          {/* Care Guide Sidebar */}
          {care_guide && (
            <Col lg={4}>
              <Card className="promo-care-guide-card sticky-top" style={{ top: '80px' }}>
                {care_guide.image_url && (
                  <Card.Img
                    variant="top"
                    src={care_guide.image_url}
                    alt={care_guide.title}
                    className="promo-care-guide-img"
                  />
                )}
                <Card.Body>
                  <Card.Title className="h5">{care_guide.title}</Card.Title>
                  {care_guide.excerpt && (
                    <Card.Text className="small text-muted">
                      {care_guide.excerpt}
                    </Card.Text>
                  )}
                  {/* Design System v1 §2.2 (Phase 3): a SECONDARY — the promo
                      page's primary is Add to Cart on its featured items. */}
                  <Link to={care_guide.link} className="ff-btn ff-btn--secondary ff-btn--block">
                    Read Full Care Guide
                  </Link>
                </Card.Body>
              </Card>
            </Col>
          )}
        </Row>
      </Container>
    </>
  );
};

export default PromoPage;
